import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function cleanRepeatedText(text: string | undefined | null): string {
  if (!text || !text.trim()) return "";

  const trimmed = text.trim();
  const words = trimmed.split(/\s+/);

  if (words.length > 1 && words.every((word) => word.toLowerCase() === words[0].toLowerCase())) {
    return words[0];
  }

  const uniqueWords = new Set(words.map((word) => word.toLowerCase()));
  if (uniqueWords.size === 1 && words.length > 3) {
    return words[0];
  }

  return trimmed;
}

export function truncateText(text: string, maxLength = 100): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength).trim()}...`;
}

export function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error parsing date:", error);
    return dateString;
  }
}

export function formatSlashDate(dateString: string | undefined): string {
  if (!dateString) return "-";

  try {
    const [datePart] = dateString.split(" ");
    let month: string, day: string, year: string;
    if (datePart.includes(".")) {
      [day, month, year] = datePart.split(".");
    } else {
      [month, day, year] = datePart.split("/");
    }
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error parsing date:", error);
    return dateString;
  }
}

/** Safe filename for PDF download; falls back to document.pdf */
export function sanitizeDownloadFilename(title: string | undefined | null, fallback = "document.pdf"): string {
  const base = cleanRepeatedText(title)?.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "").trim();
  if (!base) return fallback;
  return base.toLowerCase().endsWith(".pdf") ? base : `${base}.pdf`;
}
